import {
  MilestoneFragment,
  LazyMilestonePicker,
  type MilestonePickerMilestone$key,
  type MilestonePickerMilestone$data,
} from '@github-ui/item-picker/milestone'
import {ActionList, SelectPanel} from '@primer/react'
import {useMemo, Suspense, useState} from 'react'
import {graphql, type PreloadedQuery, readInlineData, useFragment, usePreloadedQuery} from 'react-relay'

import {LABELS} from '../../constants'
import type {MilestonesSectionFragment$key} from './__generated__/MilestonesSectionFragment.graphql'
import type {MilestonesSectionQuery} from './__generated__/MilestonesSectionQuery.graphql'
import {Milestone} from './Milestone'
import {ReadonlySectionHeader} from './ReadonlySectionHeader'
import {Section} from './Section'
import {SectionHeader} from './SectionHeader'
import {noop} from '@github-ui/noop'
import {HOTKEYS} from '@github-ui/item-picker'
import {useKeyPress} from '@github-ui/use-key-press'

export const MilestonesSectionGraphqlQuery = graphql`
  query MilestonesSectionQuery($owner: String!, $repo: String!, $number: Int!) {
    repository(owner: $owner, name: $repo) {
      issue(number: $number) {
        ...MilestonesSectionFragment
      }
    }
  }
`

const MilestonesSectionFragment = graphql`
  fragment MilestonesSectionFragment on Issue {
    repository {
      name
      owner {
        login
      }
    }
    id
    milestone {
      ...MilestonePickerMilestone
    }
    viewerCanSetMilestone
  }
`

type MilestonesSectionProps = {
  queryRef: PreloadedQuery<MilestonesSectionQuery>
  onIssueUpdate?: () => void
  singleKeyShortcutsEnabled: boolean
  prefetchInteractionDataEnabled: boolean
}

type MilestonesSectionInternalProps = Omit<MilestonesSectionProps, 'queryRef'> & {
  issue: MilestonesSectionFragment$key
}

export function MilestonesSection({queryRef, ...rest}: MilestonesSectionProps) {
  const preloadedData = usePreloadedQuery<MilestonesSectionQuery>(MilestonesSectionGraphqlQuery, queryRef)
  return preloadedData.repository && preloadedData.repository.issue ? (
    <MilestonesSectionInternal issue={preloadedData.repository.issue} {...rest} />
  ) : null
}

export function MilestonesSectionInternal({
  issue,
  onIssueUpdate,
  singleKeyShortcutsEnabled,
  prefetchInteractionDataEnabled,
}: MilestonesSectionInternalProps) {
  const {
    repository: {
      owner: {login: owner},
      name: repo,
    },
    id: issueId,
    viewerCanSetMilestone,
    milestone,
  } = useFragment(MilestonesSectionFragment, issue)

  // eslint-disable-next-line no-restricted-syntax
  const activeMilestone = readInlineData<MilestonePickerMilestone$key>(MilestoneFragment, milestone)
  const hasMilestones = activeMilestone

  const sectionHeader = useMemo(() => {
    if (!viewerCanSetMilestone) {
      return <ReadonlySectionHeader title={LABELS.sectionTitles.milestones} />
    }
    return prefetchInteractionDataEnabled ? (
      <Suspense fallback={<ReadonlySectionHeader title={LABELS.sectionTitles.milestones} />}>
        <LazyMilestonePicker
          issueId={issueId}
          repo={repo}
          owner={owner}
          readonly={!viewerCanSetMilestone}
          shortcutEnabled={singleKeyShortcutsEnabled}
          activeMilestone={activeMilestone}
          onIssueUpdate={onIssueUpdate}
          anchorElement={props => <SectionHeader title={LABELS.sectionTitles.milestones} buttonProps={props} />}
        />
      </Suspense>
    ) : (
      <LazyMilestonePickerOnClick
        issueId={issueId}
        repo={repo}
        owner={owner}
        viewerCanSetMilestone={viewerCanSetMilestone}
        singleKeyShortcutsEnabled={singleKeyShortcutsEnabled}
        activeMilestone={activeMilestone}
        onIssueUpdate={onIssueUpdate}
      />
    )
  }, [
    activeMilestone,
    prefetchInteractionDataEnabled,
    issueId,
    onIssueUpdate,
    owner,
    repo,
    singleKeyShortcutsEnabled,
    viewerCanSetMilestone,
  ])

  return (
    <Section sectionHeader={sectionHeader} emptyText={hasMilestones ? undefined : LABELS.emptySections.milestones}>
      <ActionList variant={'full'}>
        <Milestone milestone={activeMilestone} />
      </ActionList>
    </Section>
  )
}

type LazyMilestonePickerOnClickProps = {
  issueId: string
  repo: string
  owner: string
  viewerCanSetMilestone: boolean
  singleKeyShortcutsEnabled: boolean
  activeMilestone: MilestonePickerMilestone$data | null
  onIssueUpdate?: () => void
}

function LazyMilestonePickerOnClick({
  issueId,
  repo,
  owner,
  viewerCanSetMilestone,
  singleKeyShortcutsEnabled,
  activeMilestone,
  onIssueUpdate,
}: LazyMilestonePickerOnClickProps) {
  const [wasTriggered, setWasTriggered] = useState(false)
  const [searchText, setSearchText] = useState('')

  useKeyPress(singleKeyShortcutsEnabled ? [HOTKEYS.milestonePicker] : [], (e: KeyboardEvent) => {
    if (!wasTriggered) {
      setWasTriggered(true)
      e.preventDefault()
    }
  })

  if (!wasTriggered) {
    return (
      <SectionHeader title={LABELS.sectionTitles.milestones} buttonProps={{onClick: () => setWasTriggered(true)}} />
    )
  }
  return (
    <Suspense
      fallback={
        <SelectPanel
          loading={true}
          placeholderText={'Filter milestones'}
          open
          onOpenChange={noop}
          items={[]}
          onSelectedChange={noop}
          filterValue={searchText}
          onFilterChange={setSearchText}
          selected={undefined}
          renderAnchor={props => <SectionHeader title={LABELS.sectionTitles.milestones} buttonProps={props} />}
          overlayProps={{width: 'medium', height: 'auto'}}
        />
      }
    >
      <LazyMilestonePicker
        issueId={issueId}
        repo={repo}
        owner={owner}
        readonly={!viewerCanSetMilestone}
        shortcutEnabled={singleKeyShortcutsEnabled}
        activeMilestone={activeMilestone}
        onIssueUpdate={onIssueUpdate}
        triggerOpen={true}
        initialFilter={searchText}
        anchorElement={props => <SectionHeader title={LABELS.sectionTitles.milestones} buttonProps={props} />}
      />
    </Suspense>
  )
}

try{ MilestonesSection.displayName ||= 'MilestonesSection' } catch {}
try{ MilestonesSectionInternal.displayName ||= 'MilestonesSectionInternal' } catch {}
try{ LazyMilestonePickerOnClick.displayName ||= 'LazyMilestonePickerOnClick' } catch {}